import {FormOverride} from '@wix/form-viewer/dist/types/types';
import {FormError, FormViewerHandle} from '@wix/form-viewer/widget';
import {ValueType} from '@wix/form-fields/dist/types/ui/form/types';
import {CheckoutSettingsModel} from '../../../domain/models/checkout/checkoutSettings/CheckoutSettings.model';
import {ILocaleKeys} from '../../../locale-keys/LocaleKeys';
import {mergeDeep} from '../../../domain/utils/mergeDeep';
import {FormOverrides, FullNameValue, AddressTarget, FormFieldTarget} from '../../../types/checkoutApp.types';
import {FormValues, MultilineAddressValue} from '@wix/form-fields';
import {COUNTRIES_EXCLUDED_FROM_AUTOCOMPLETE, DEFAULT_COUNTRY} from '../constants';
import {ContactModel} from '../../../domain/models/checkout/Cart/Contact.model';
import {AddressModel} from '../../../domain/models/checkout/Cart/Address.model';
import {type CountryCode} from '@wix/form-multiline-address';

export const mandatoryOverride: FormOverride = {
  hidden: false,
  required: true,
};

export const hiddenOverride: FormOverride = {
  hidden: true,
  required: false,
};

export interface FormProps {
  formRef: React.MutableRefObject<FormViewerHandle | null>;
  formValues: FormValues;
  setFormValues: (value: ((prevState: FormValues) => FormValues) | FormValues) => void;
  formErrors: FormError[];
  setFormErrors: (value: ((prevState: FormError[]) => FormError[]) | FormError[]) => void;
}

export function mergeOverrides(...overrides: FormOverrides[]): FormOverrides {
  return mergeDeep(...(overrides as {[key: string]: object}[]));
}

export function getSettingsOverrides({
  checkoutSettings,
  doesFormHaveMultilineAddress,
}: {
  checkoutSettings: Pick<CheckoutSettingsModel, 'isLocalDeliveryByZipCodeEnabled'>;
  doesFormHaveMultilineAddress?: boolean;
}): FormOverrides {
  if (doesFormHaveMultilineAddress) {
    return {
      address: checkoutSettings.isLocalDeliveryByZipCodeEnabled
        ? {
            [AddressTarget.postalCode]: mandatoryOverride,
          }
        : {},
    };
  }

  return {
    address: {
      postal_code: checkoutSettings.isLocalDeliveryByZipCodeEnabled ? mandatoryOverride : {},
    },
  };
}

export function getHideCountryOverride(): FormOverrides {
  return {address: {country: {hidden: true, required: false}}};
}

export function getDeliveryCountriesOverrides(
  enabledDeliveryCountries: CheckoutSettingsModel['enabledDeliveryCountries']
): FormOverrides {
  if (enabledDeliveryCountries.length) {
    return {address: {country: {countries: enabledDeliveryCountries}}};
  }
  return {};
}

export function getExcludeGoogleAutoCompleteOverrides({
  excludeGoogleAutoComplete,
  doesFormHaveMultilineAddress,
  country,
  moveCheckoutToViewerContext,
}: {
  excludeGoogleAutoComplete?: boolean;
  doesFormHaveMultilineAddress?: boolean;
  country?: CountryCode | null;
  moveCheckoutToViewerContext: boolean;
}): FormOverrides {
  const shouldExcludeAutoComplete =
    moveCheckoutToViewerContext && country && COUNTRIES_EXCLUDED_FROM_AUTOCOMPLETE.includes(country)
      ? true
      : excludeGoogleAutoComplete;
  return {
    address: {
      ...(doesFormHaveMultilineAddress
        ? {
            [AddressTarget.addressLine]: {
              autocomplete: !shouldExcludeAutoComplete,
            },
            [AddressTarget.streetName]: {
              autocomplete: !shouldExcludeAutoComplete,
            },
          }
        : {
            [AddressTarget.address_line]: {
              autocomplete: !shouldExcludeAutoComplete,
            },
            [AddressTarget.street_name]: {
              autocomplete: !shouldExcludeAutoComplete,
            },
          }),
    },
  };
}

export function getLabelsOverrides({localeKeys}: {localeKeys: ILocaleKeys}): FormOverrides {
  return {
    full_name: {
      first_name: {label: localeKeys.checkout.address_information.first_name_label()},
      last_name: {label: localeKeys.checkout.address_information.last_name_label()},
    },
    phone: {label: localeKeys.checkout.address_information.phone_number_label()},
    company_name: {label: localeKeys.checkout.address_information.company_label()},
  };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getAddressFormInitialState(
  address?: AddressModel,
  country?: string,
  doesFormHaveMultilineAddress?: boolean
): FormValues {
  const subdivision = address?.subdivision?.includes('-') ? address?.subdivision?.split('-')[1] : address?.subdivision;

  return {
    address: {
      country,
      city: address?.city ?? '',
      ...(subdivision ? {subdivision} : {}),
      ...(doesFormHaveMultilineAddress
        ? {
            ...(address?.postalCode ? {[AddressTarget.postalCode]: address.postalCode} : {}),
            ...(address?.addressLine ? {[AddressTarget.addressLine]: address.addressLine} : {}),
            ...(address?.streetAddress?.name ? {[AddressTarget.streetName]: address.streetAddress.name} : {}),
            ...(address?.streetAddress?.number ? {[AddressTarget.streetNumber]: address.streetAddress.number} : {}),
            ...(address?.addressLine2 ? {[AddressTarget.addressLine2]: address?.addressLine2} : {}),
          }
        : {
            ...(address?.postalCode ? {[AddressTarget.postal_code]: address.postalCode} : {}),
            ...(address?.addressLine ? {[AddressTarget.address_line]: address.addressLine} : {}),
            ...(address?.streetAddress?.name ? {[AddressTarget.street_name]: address.streetAddress.name} : {}),
            ...(address?.streetAddress?.number ? {[AddressTarget.street_number]: address.streetAddress.number} : {}),
            ...(address?.addressLine2 ? {[AddressTarget.address_line_2]: address?.addressLine2} : {}),
          }),
    },
  };
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function getAddressFromAddressFormValues(addressFormValues: FormValues, doesFormHaveMultilineAddress?: boolean) {
  const address = addressFormValues.address as MultilineAddressValue;
  const postalCode = doesFormHaveMultilineAddress ? address.postalCode : address.postal_code;
  const addressLine = doesFormHaveMultilineAddress ? address.addressLine : address.address_line;
  const addressLine2 = doesFormHaveMultilineAddress ? address.addressLine2 : address.address_line_2;
  const streetName = doesFormHaveMultilineAddress ? address.streetName : address.street_name;
  const streetNumber = doesFormHaveMultilineAddress ? address.streetNumber : address.street_number;
  const streetAddress =
    streetName || streetNumber
      ? {
          name: (streetName as string) || /* istanbul ignore next: test forms */ undefined,
          number: (streetNumber as string) || /* istanbul ignore next: test forms */ undefined,
        }
      : undefined;

  return {
    country: address.country as string,
    city: address.city as string,
    ...(address.subdivision
      ? {
          subdivision: `${address.country as string}-${address.subdivision}`,
        }
      : {}),
    ...(postalCode ? {postalCode} : /* istanbul ignore next: test forms */ {}),
    ...(addressLine ? {addressLine} : {}),
    ...(streetAddress ? {streetAddress} : {}),
    ...(addressLine2 ? {addressLine2} : {}),
  };
}

export function getCountryFromAddressFormValues(addressFormValues: FormValues): string {
  const address = addressFormValues.address as MultilineAddressValue;
  return address.country as string;
}

export function isSubdivisionsEqual(subdivision1?: string | null, subdivision2?: string | null): boolean {
  if (subdivision2 && subdivision1) {
    return subdivision1.includes(subdivision2) || /* istanbul ignore next */ subdivision2.includes(subdivision1);
  }

  return subdivision1 === subdivision2;
}

export function getContactFormInitialState({contact, country}: {contact?: ContactModel; country?: string}): FormValues {
  return {
    full_name: {
      first_name: contact?.firstName ?? '',
      last_name: contact?.lastName ?? '',
      country: country ?? /* istanbul ignore next */ DEFAULT_COUNTRY,
    },
    ...{phone: contact?.phone ?? ''},
    ...{company_name: contact?.company ?? ''},
  };
}

export function getContactFormCurrentStateWithUpdatedCountry({
  contactFormValues,
  country,
}: {
  contactFormValues: FormValues;
  country?: string;
}): FormValues {
  const fullName = contactFormValues.full_name as FullNameValue;
  return {
    ...contactFormValues,
    full_name: {
      ...fullName,
      country,
    },
  };
}

export function getContactDetailsFromContactFormValues(contactFormValues: FormValues) {
  const fullName = contactFormValues.full_name as FullNameValue;
  return {
    firstName: fullName.first_name as string,
    lastName: fullName.last_name as string,
    ...(contactFormValues.phone ? {phone: contactFormValues.phone as string} : {}),
    ...(contactFormValues.company_name ? {company: contactFormValues.company_name as string} : {}),
  };
}

export function getCountryCode(formValues: FormValues): CountryCode | undefined {
  const address = formValues[FormFieldTarget.address] as {[target: string]: ValueType} | undefined;
  const countryCode = address ? address[AddressTarget.country] : undefined;
  return countryCode ? (countryCode as CountryCode) : undefined;
}
